import { useEffect, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useResolvedMedia } from '../media/useResolvedMedia';
import { HostedPhotoGuard } from '../media/HostedPhotoGuard';
import { AvatarImageLoader, IAvatarImageStatus } from './AvatarImageLoader';
import { IAvatarIdentity } from './IAvatarIdentity';

// Held against the url it describes: a bare status would let a url change render the new image
// while the previous url's 'loaded' was still in state, and React commits that frame before the
// effect runs, so an unvalidated image would reach the DOM.
const useImageLoadStatus = (url: string | undefined): IAvatarImageStatus => {
    const loader = useInstance(AvatarImageLoader);
    const [result, setResult] = useState<{ url: string; status: IAvatarImageStatus }>();

    useEffect(() => {
        if (!url) {
            return;
        }
        return loader.load({ url, onStatus: status => setResult({ url, status }) });
    }, [url, loader]);

    return result && result.url === url ? result.status : 'loading';
};

/**
 * The picture an avatar should paint, and how far it has got. Only an uploaded photo is authorised
 * first; an illustration ships with the app, so pointing it at the api would be wrong. A photo that
 * cannot be authorised and one that cannot be loaded are the same outcome for the person looking at
 * it, so both report 'failed' rather than quietly standing in the no-photo appearance, and so does a
 * photo we host that arrived without its link, which is never read.
 */
export const useAvatarImage = (params: {
    photo?: string;
    photoHref?: string;
    photoHasNoAuditedRoute?: IAvatarIdentity['photoHasNoAuditedRoute'];
    illustration?: string;
}): {
    url?: string;
    status: IAvatarImageStatus;
} => {
    const { photo, photoHref, photoHasNoAuditedRoute, illustration } = params;
    const guard = useInstance(HostedPhotoGuard);
    const refused = !photoHasNoAuditedRoute && guard.refuses({ photo, photoHref });

    useEffect(() => {
        if (refused && photo) {
            guard.reportRefused(photo);
        }
    }, [refused, photo, guard]);

    const media = useResolvedMedia({ url: refused ? undefined : photo, href: photoHref });

    const resolvedPhoto = media.status === 'resolved' ? media.url : undefined;
    const url = photo ? resolvedPhoto : illustration;
    const status = useImageLoadStatus(url);

    if (photo && (refused || media.status === 'unavailable')) {
        return { url: undefined, status: 'failed' };
    }
    return { url, status };
};
